import { Fragment, useCallback, useState, type ReactNode } from 'react';
import type { AskTheWireAnswer, AskTheWireEvidence } from '@tsr/shared-types';
import type { SignalFeedFilters, SignalFeedFocus } from './lib/mediaSignalFeed';
import { authedFetch } from './api';

/**
 * ASK THE WIRE (#299) — the operator query box atop the News & Social tab. A
 * free-text question goes to POST /api/ask-the-wire; the answer is GROUNDED ONLY
 * in retrieved signals + briefs + anomalies, cites click-through evidence, or
 * ABSTAINS honestly ("nothing in the wire about that"), or DEGRADES to a plain
 * list (on a cost-cap hit or any failure — #301). The retrieval is bounded by the
 * operator's current filters (hazard + freshness window) and any incident-focus
 * bbox — the same context the rest of the tab already uses.
 *
 * READ-ONLY by construction: the answer and its evidence carry NO pin / forward /
 * promote affordance — a query result is never auto-urgent, never an anomaly,
 * never forwardable as fact. The honesty chrome ("Unconfirmed signal intelligence")
 * and the per-source provenance are always shown.
 */

const SOURCE_CHIP: Record<string, { label: string; cls: string }> = {
  news: { label: 'news', cls: 'bg-sky-900/60 text-sky-200' },
  social: { label: 'social', cls: 'bg-violet-900/60 text-violet-200' },
  official: { label: 'official', cls: 'bg-emerald-900/60 text-emerald-200' },
  scanner: { label: 'scanner', cls: 'bg-amber-900/60 text-amber-200' },
  brief: { label: 'brief', cls: 'bg-indigo-900/60 text-indigo-200' },
  anomaly: { label: 'spike', cls: 'bg-rose-900/60 text-rose-200' },
};

function chipFor(e: AskTheWireEvidence): { label: string; cls: string } {
  return SOURCE_CHIP[e.sourceType ?? e.kind] ?? { label: e.kind, cls: 'bg-slate-800 text-slate-300' };
}

function relTime(iso: string): string {
  const ms = Date.now() - Date.parse(iso);
  if (!Number.isFinite(ms)) return '';
  const m = Math.round(ms / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

/** Render answer prose, turning [n] citation markers into small index badges. */
function renderProse(text: string): ReactNode[] {
  const parts = text.split(/(\[\d+\])/g);
  return parts.map((p, i) => {
    const m = /^\[(\d+)\]$/.exec(p);
    if (m) {
      return (
        <sup
          key={i}
          data-testid="atw-citation-marker"
          className="mx-px rounded bg-cyan-900/70 px-1 text-[9px] font-semibold text-cyan-200"
        >
          {m[1]}
        </sup>
      );
    }
    return <Fragment key={i}>{p}</Fragment>;
  });
}

function EvidenceList({
  evidence,
  cited,
  label,
}: {
  evidence: readonly AskTheWireEvidence[];
  cited: ReadonlySet<string>;
  label: string;
}): JSX.Element {
  return (
    <div className="mt-1.5 border-t border-cyan-800/40 pt-1.5">
      <p className="mb-1 text-[9px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <ul data-testid="atw-evidence" className="space-y-1">
        {evidence.map((e, i) => {
          const chip = chipFor(e);
          const isCited = cited.has(e.id);
          return (
            <li
              key={e.id}
              data-testid="atw-evidence-item"
              data-cited={isCited ? 'true' : 'false'}
              className={`flex items-baseline gap-1.5 text-[11px] ${isCited ? '' : 'opacity-80'}`}
            >
              <span className="shrink-0 text-[9px] font-semibold text-cyan-300/80">[{i + 1}]</span>
              <span className={`shrink-0 rounded px-1 py-px text-[8px] font-semibold uppercase ${chip.cls}`}>
                {chip.label}
              </span>
              {e.origin && (
                <span className="shrink-0 rounded bg-slate-800 px-1 py-px text-[9px] uppercase text-slate-400">
                  {e.origin}
                </span>
              )}
              {e.url ? (
                <a
                  href={e.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="min-w-0 truncate text-sky-400 hover:text-sky-300"
                >
                  {e.title ?? e.url}
                </a>
              ) : (
                <span className="min-w-0 truncate text-slate-300">{e.title ?? '(untitled)'}</span>
              )}
              {e.geoName && <span className="shrink-0 text-[10px] text-slate-500">· {e.geoName}</span>}
              <span className="ml-auto shrink-0 text-[10px] text-slate-500">{relTime(e.observedAt)}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function AskTheWire({
  filters,
  focus,
}: {
  filters: SignalFeedFilters;
  focus: SignalFeedFocus | null;
}): JSX.Element {
  const [question, setQuestion] = useState('');
  const [busy, setBusy] = useState(false);
  const [answer, setAnswer] = useState<AskTheWireAnswer | null>(null);
  const [error, setError] = useState<string | null>(null);

  const ask = useCallback(async (): Promise<void> => {
    const q = question.trim();
    if (q.length < 3 || busy) return;
    setBusy(true);
    setError(null);
    try {
      const body: Record<string, unknown> = { question: q, hours: filters.sinceHours };
      if (filters.hazard !== 'all') body.hazard = [filters.hazard];
      if (focus) body.bbox = { west: focus.west, south: focus.south, east: focus.east, north: focus.north };
      const res = await authedFetch('/api/ask-the-wire', {
        method: 'POST',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.status === 400) throw new Error('bad question');
      const data = (await res.json()) as AskTheWireAnswer;
      setAnswer(data);
    } catch {
      // Never a dead box — surface a retto message, leave any prior answer cleared.
      setError('Ask the Wire is unavailable — please retry.');
      setAnswer(null);
    } finally {
      setBusy(false);
    }
  }, [question, busy, filters, focus]);

  /** Reset to the empty state — wipes the question, the answer, and any error
   *  (#373). The operator can clear a stale answer instead of being stuck with
   *  it (or hand-deleting the text) before asking the next question. */
  const reset = useCallback((): void => {
    setQuestion('');
    setAnswer(null);
    setError(null);
  }, []);

  const cited = new Set(answer?.citationIds ?? []);
  // The Clear control only appears when there's something to clear — a typed
  // question, a rendered answer, or an error.
  const hasContent = question.trim().length > 0 || answer != null || error != null;

  return (
    <div
      data-testid="ask-the-wire"
      className="mb-2 rounded-md border border-cyan-800/50 bg-cyan-950/20 p-2"
    >
      <div className="flex items-center gap-1.5">
        <span className="shrink-0 rounded bg-cyan-900/70 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-cyan-200">
          ✦ Ask the Wire
        </span>
        <span className="text-[10px] text-slate-500">grounded in the wire, or it abstains</span>
      </div>
      <div className="mt-1.5 flex items-center gap-1.5">
        <input
          data-testid="ask-the-wire-input"
          type="text"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void ask();
          }}
          placeholder="e.g. any reports of flooding near Calgary?"
          maxLength={500}
          className="min-w-0 flex-1 rounded border border-slate-700 bg-slate-900/70 px-2 py-1 text-[12px] text-slate-100 placeholder:text-slate-600 focus:border-cyan-600 focus:outline-none"
        />
        <button
          type="button"
          data-testid="ask-the-wire-submit"
          onClick={() => void ask()}
          disabled={busy || question.trim().length < 3}
          className="shrink-0 rounded bg-cyan-700 px-2.5 py-1 text-[11px] font-medium text-cyan-50 hover:bg-cyan-600 disabled:cursor-not-allowed disabled:bg-slate-700 disabled:text-slate-500"
        >
          {busy ? '…' : 'Ask'}
        </button>
        {hasContent && (
          <button
            type="button"
            data-testid="ask-the-wire-clear"
            onClick={reset}
            title="Clear the question and answer"
            className="shrink-0 rounded border border-slate-700 px-2 py-1 text-[11px] text-slate-300 hover:border-slate-500 hover:text-slate-100"
          >
            Clear
          </button>
        )}
      </div>

      {error && (
        <p data-testid="ask-the-wire-error" className="mt-1.5 text-[11px] text-slate-400">
          {error}
        </p>
      )}

      {answer && !error && (
        <div className="mt-1.5">
          {answer.status === 'answered' && answer.answer != null ? (
            <div data-testid="ask-the-wire-answer">
              <p className="text-[12px] leading-snug text-slate-100">{renderProse(answer.answer)}</p>
              <EvidenceList evidence={answer.evidence} cited={cited} label="Evidence" />
            </div>
          ) : answer.status === 'abstained' ? (
            <div data-testid="ask-the-wire-abstain">
              <p className="text-[12px] font-medium text-slate-300">{answer.notConfirmedLine}</p>
              {answer.evidence.length > 0 && (
                <EvidenceList evidence={answer.evidence} cited={cited} label="Closest matches in the wire" />
              )}
            </div>
          ) : (
            <div data-testid="ask-the-wire-degrade">
              <p className="text-[12px] font-medium text-amber-300/90">{answer.notConfirmedLine}</p>
              {(answer.degradeReason === 'operator_cap' || answer.degradeReason === 'global_cap') && (
                <p className="mt-0.5 text-[10px] text-slate-400">Daily query limit reached — showing matching signals.</p>
              )}
              {answer.evidence.length > 0 && (
                <EvidenceList evidence={answer.evidence} cited={cited} label="Matching wire items" />
              )}
            </div>
          )}

          {/* Honesty + cost chrome — always shown, never auto-urgent. */}
          <div data-testid="ask-the-wire-chrome" className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-slate-500">
            <span className="italic text-cyan-300/70">Unconfirmed signal intelligence · read-only</span>
            {answer.status === 'answered' && (
              <span className="italic">AI answer · {answer.retrievedCount} wire items</span>
            )}
            <span className="ml-auto" title="Per-operator daily query budget (cost cage #301)">
              {answer.budget.operatorRemaining} queries left today
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
